import {
  MapStyle,
  WorldMapGeoJSONLayerConfig,
  WorldMapGeoJSONLayerField,
} from "@components/world-map/world-map-types";
import { blue, neutral, orange, WorldMapMarker } from "@flat-ui";

/** Width of the world map marker icon in pixels. */
export const WORLD_MAP_MARKER_WIDTH_PX: number = 26;

/** Height of the world map marker icon in pixels. */
export const WORLD_MAP_MARKER_HEIGHT_PX: number = 32;

/** Hex color baked into the `WorldMapMarker` SVG. Used as the replacement anchor for recoloring. */
const BASE_MARKER_COLOR: string = blue[500];

/**
 * Marker fill colors per map style, paired so the default and the hover/selected
 * highlight always come from the same color ramp.
 * - The light `colored` basemap keeps the brand blue.
 * - `satellite` switches to a warm orange so markers stay readable on top of
 *   satellite imagery, where dark blue tends to blend into water, shadows and vegetation.
 */
export const MARKER_COLORS_BY_MAP_STYLE: Record<
  MapStyle,
  { default: string; highlight: string }
> = {
  colored: { default: blue[500], highlight: blue[700] },
  satellite: { default: orange[400], highlight: orange[600] },
};

/**
 * Returns the marker SVG data URL recolored with the given fill color.
 * Falls back to the original (blue) marker when the requested color matches the baked-in color.
 */
export function getMarkerUrl(color: string): string {
  if (color === BASE_MARKER_COLOR) {
    return WorldMapMarker;
  }
  return WorldMapMarker.replace(
    encodeURIComponent(BASE_MARKER_COLOR),
    encodeURIComponent(color)
  );
}

/**
 * Shared picture-marker geometry (size + y-offset) used by both the data layer
 * renderer and the hover/selected highlight overlay.
 */
function getBaseMarkerSymbolProps(
  color: string
): __esri.PictureMarkerSymbolProperties {
  return {
    url: getMarkerUrl(color),
    width: `${WORLD_MAP_MARKER_WIDTH_PX}px`,
    height: `${WORLD_MAP_MARKER_HEIGHT_PX}px`,
    // Offset the marker vertically so that the point of the marker is at the exact coordinates
    // instead of the center of the marker image.
    yoffset: `${WORLD_MAP_MARKER_HEIGHT_PX / 2}px`,
  };
}

/** Builds the picture-marker symbol for the data layer renderer, colored for the given map style. */
export function getMarkerSymbol(
  mapStyle: MapStyle
): __esri.PictureMarkerSymbolProperties & { type: "picture-marker" } {
  return {
    type: "picture-marker",
    ...getBaseMarkerSymbolProps(MARKER_COLORS_BY_MAP_STYLE[mapStyle].default),
  };
}

/**
 * Builds the picture-marker symbol used for the hover/selected highlight overlay.
 *
 * @param mapStyle - active basemap; selects the highlight color.
 * @param scale - uniform size multiplier applied to the base marker (1 = original size).
 */
export function getHighlightMarkerSymbolProps(
  mapStyle: MapStyle,
  scale: number = 1
): __esri.PictureMarkerSymbolProperties {
  const width = WORLD_MAP_MARKER_WIDTH_PX * scale;
  const height = WORLD_MAP_MARKER_HEIGHT_PX * scale;
  return {
    url: getMarkerUrl(MARKER_COLORS_BY_MAP_STYLE[mapStyle].highlight),
    width: `${width}px`,
    height: `${height}px`,
    // Keep the marker tip anchored at the coordinate (yoffset = height / 2).
    yoffset: `${height / 2}px`,
  };
}

/** Builds the GeoJSONLayer configuration with the marker colored for the given map style. */
export function getWorldMapGeoJSONLayerConfig(
  mapStyle: MapStyle
): WorldMapGeoJSONLayerConfig {
  return {
    outFields: ["*"],
    // Marker
    renderer: {
      type: "simple",
      symbol: getMarkerSymbol(mapStyle),
    },
    // Cluster
    featureReduction: {
      type: "cluster",
      // eslint-disable-next-line @typescript-eslint/naming-convention
      popupEnabled: false,
      clusterRadius: "100px",
      clusterMinSize: "32px",
      clusterMaxSize: "32px",
      labelingInfo: [
        // Label showing number of clustered features
        {
          deconflictionStrategy: "none",
          labelPlacement: "center-center",
          labelExpressionInfo: {
            expression: "$feature.cluster_count",
          },
          symbol: {
            type: "text",
            // Move the label down to compensate for the y-offset applied to the marker symbol,
            // so that the label is centered within the cluster.
            yoffset: "-3px",
            color: neutral[0],
            font: {
              size: "12px",
              weight: "bold",
            },
          },
        },
      ],
    },
  };
}

/** Latitude to center the world map view on initialization */
export const WORLD_MAP_VIEW_CENTER_LAT: number = 50;

/** Longitude to center the world map view on initialization */
export const WORLD_MAP_VIEW_CENTER_LON: number = 10;

/** Initial zoom level for the world map view on initialization */
export const WORLD_MAP_INITIAL_ZOOM: number = 0;

/** Maximum allowed zoom level. */
export const WORLD_MAP_MAX_ZOOM: number = 20;

/** Zoom level to focus on a specific position on the world map */
export const WORLD_MAP_FOCUS_ZOOM: number = 16;

/** Default fields for the world map GeoJSON layer */
export const WORLD_MAP_DEFAULT_FIELDS: WorldMapGeoJSONLayerField[] = [
  { name: "name", type: "string" },
  { name: "id", type: "string" },
];

/** ArcGIS named basemap id used for each map style. */
export const ARCGIS_BASEMAP_ID_BY_MAP_STYLE: Record<MapStyle, string> = {
  colored: "streets-vector",
  satellite: "hybrid",
};

/** Default map style for the world map */
export const WORLD_MAP_DEFAULT_MAP_STYLE: MapStyle = "colored";
